// Phase C — Export Jobs Bell
// Polls /api/exports/jobs every 10s, shows badge with `ready` count, popup
// with last 10 jobs and quick download/dismiss buttons. Toast on transition
// `running` → `ready`.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileDown, Loader2, CheckCircle2, XCircle, Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 10000; // 10 seconds

function fmtAge(iso) {
  if (!iso) return "—";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "gerade eben";
    if (m < 60) return `vor ${m} Min.`;
    const h = Math.floor(m / 60);
    if (h < 24) return `vor ${h} Std.`;
    return `vor ${Math.floor(h / 24)} Tagen`;
  } catch {
    return iso;
  }
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function StatusIcon({ status }) {
  if (status === "ready") return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
  if (status === "failed") return <XCircle className="w-4 h-4 text-rose-400" />;
  if (status === "running") return <Loader2 className="w-4 h-4 animate-spin text-blue-400" />;
  return <Clock className="w-4 h-4 text-amber-400" />;
}

export default function ExportJobsBell() {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [readyCount, setReadyCount] = useState(0);
  const seenReady = useRef(new Set());
  const inited = useRef(false);

  async function load() {
    try {
      const { data } = await api.get("/exports/jobs", { params: { limit: 20 } });
      setJobs(data.jobs || []);
      setReadyCount(data.ready_count || 0);
      // Toast when a job *transitions* to ready while bell is mounted
      if (inited.current) {
        for (const j of data.jobs || []) {
          if (j.status === "ready" && !seenReady.current.has(j.id)) {
            toast.success(`${j.label} ist bereit`, {
              description: j.file_name,
              action: {
                label: "Herunterladen",
                onClick: () => downloadJob(j.id),
              },
              duration: 8000,
            });
          }
        }
      }
      seenReady.current = new Set(
        (data.jobs || []).filter((j) => j.status === "ready").map((j) => j.id)
      );
      inited.current = true;
    } catch (e) {
      // Non-critical bell — log once for diagnostics, but don't toast.
      if (!inited.current) {
        // eslint-disable-next-line no-console
        console.warn("[ExportJobsBell] poll failed", e?.response?.status || e?.message);
        inited.current = true;
      }
    }
  }

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function downloadJob(jobId) {
    try {
      const res = await api.get(`/exports/jobs/${jobId}/download`, { responseType: "blob" });
      const job = jobs.find((j) => j.id === jobId);
      const fname = job?.file_name || `export_${jobId}.bin`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  }

  async function dismiss(jobId, e) {
    e?.stopPropagation?.();
    try {
      await api.delete(`/exports/jobs/${jobId}`);
      load();
    } catch {}
  }

  // Hide entirely if user never used any export
  if (!jobs.length && !open) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="export-jobs-bell"
        title="Export-Downloads"
        className="relative p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors"
      >
        <FileDown className="w-4 h-4" />
        {readyCount > 0 && (
          <span
            data-testid="export-jobs-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-500 text-[10px] font-bold text-white flex items-center justify-center"
          >
            {readyCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-30"
            aria-hidden
          />
          {/* Panel */}
          <div
            data-testid="export-jobs-panel"
            className="absolute right-0 top-full mt-2 w-[420px] max-h-[520px] overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 shadow-xl z-40"
          >
            <div className="sticky top-0 px-4 py-3 border-b border-zinc-800 bg-zinc-900 flex items-center justify-between">
              <div className="text-sm font-semibold text-zinc-100">Export-Downloads</div>
              <Link
                to="/exports/downloads"
                onClick={() => setOpen(false)}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Alle anzeigen →
              </Link>
            </div>

            {jobs.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-zinc-500">
                Noch keine Export-Jobs.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {jobs.slice(0, 10).map((j) => (
                  <li
                    key={j.id}
                    data-testid={`export-job-row-${j.id}`}
                    className="px-4 py-3 hover:bg-zinc-800/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="pt-0.5"><StatusIcon status={j.status} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-100 truncate">{j.label}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {fmtAge(j.created_at)}
                          {j.file_size != null && <> · {fmtBytes(j.file_size)}</>}
                        </div>
                        {j.error && (
                          <div className="text-[11px] text-rose-400 mt-1 truncate" title={j.error}>
                            {j.error}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {j.status === "ready" && (
                          <button
                            onClick={() => downloadJob(j.id)}
                            data-testid={`export-job-download-${j.id}`}
                            title="Herunterladen"
                            className="p-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => dismiss(j.id, e)}
                          data-testid={`export-job-dismiss-${j.id}`}
                          title="Entfernen"
                          className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800 rounded"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
